import React, { useState, useEffect } from 'react';
import { dateTimeToMonDDYYYYWithTime } from '../utils/dateUtils';

const IconPlus = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IconRefresh = ({ spinning }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={spinning ? { animation: 'asm-spin 0.9s linear infinite' } : undefined}
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const IconOpenNew = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
  </svg>
);

const AssignmentSelectorModal = ({ 
  isOpen, 
  onClose, 
  selectedAssignments, 
  onSelectionChange,
  userAssignments = [],
  onRefreshAssignments,
  allowMultiple = false,
  isProjectBuilder = false,
  onTaxonomyInheritance = null
}) => {
  const [assignments, setAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSelectedAssignments, setLocalSelectedAssignments] = useState([]);
  const [showInheritancePopup, setShowInheritancePopup] = useState(false);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(null); // 'success', 'error', or null

  useEffect(() => {
    if (isOpen) {
      setAssignments(userAssignments);
      // Sync local selection state with prop when modal opens
      setLocalSelectedAssignments(selectedAssignments || []);
      // Reset refresh status when modal opens
      setRefreshStatus(null);
    }
  }, [isOpen, userAssignments, selectedAssignments]);

  const handleRefreshAssignments = async () => {
    if (!onRefreshAssignments || isRefreshing) return;
    
    setIsRefreshing(true);
    setRefreshStatus(null);
    
    try {
      await onRefreshAssignments();
      setRefreshStatus('success');
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setRefreshStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error refreshing assignments:', error);
      setRefreshStatus('error');
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        setRefreshStatus(null);
      }, 5000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAssignmentToggle = (assignment) => {
    if (allowMultiple) {
      // Multiple selection mode: toggle the assignment in the list
      const isSelected = localSelectedAssignments.some(a => a.id === assignment.id);
      let newSelection;
      
      if (isSelected) {
        // Remove assignment if already selected
        newSelection = localSelectedAssignments.filter(a => a.id !== assignment.id);
      } else {
        // Add assignment if not selected
        newSelection = [...localSelectedAssignments, assignment];
      }
      
      // Update local state immediately for UI responsiveness
      setLocalSelectedAssignments(newSelection);
      
      // For ProjectBuilder, show inheritance popup when assignments are selected AND they have taxonomy data
      if (isProjectBuilder && newSelection.length > 0 && onTaxonomyInheritance) {
        // Check if any of the selected assignments have taxonomy data
        const assignmentsWithTaxonomy = newSelection.filter(assignment => 
          assignment.taxonomy && Array.isArray(assignment.taxonomy) && assignment.taxonomy.length > 0
        );
        
        console.log('AssignmentSelectorModal - Checking taxonomy inheritance:', {
          totalSelected: newSelection.length,
          withTaxonomy: assignmentsWithTaxonomy.length,
          assignments: newSelection.map(a => ({
            id: a.id,
            title: a.title,
            taxonomy: a.taxonomy,
            taxonomyType: typeof a.taxonomy,
            taxonomyLength: Array.isArray(a.taxonomy) ? a.taxonomy.length : 'N/A'
          }))
        });
        
        if (assignmentsWithTaxonomy.length > 0) {
          setPendingAssignments(newSelection);
          setShowInheritancePopup(true);
        } else {
          // No taxonomy data, just use assignments as-is
          onSelectionChange(newSelection);
        }
      } else if (!isProjectBuilder && onTaxonomyInheritance) {
        // For CourseBuilder: check if the assignment has taxonomy data and show inheritance popup
        const assignment = newSelection[0]; // Single assignment for CourseBuilder
        if (assignment.taxonomy && Array.isArray(assignment.taxonomy) && assignment.taxonomy.length > 0) {
          setPendingAssignments(newSelection);
          setShowInheritancePopup(true);
        } else {
          // No taxonomy data, just use assignment as-is
          onSelectionChange(newSelection);
        }
      } else {
        // Also notify parent component immediately for non-ProjectBuilder usage
        onSelectionChange(newSelection);
      }
    } else {
      // Single selection mode: check for taxonomy inheritance even in single mode
      if (!isProjectBuilder && onTaxonomyInheritance) {
        // For CourseBuilder: check if the assignment has taxonomy data
        if (assignment.taxonomy && Array.isArray(assignment.taxonomy) && assignment.taxonomy.length > 0) {
          setPendingAssignments([assignment]);
          setShowInheritancePopup(true);
        } else {
          // No taxonomy data, just use assignment as-is
          onSelectionChange([assignment]);
          onClose();
        }
      } else {
        // Standard single selection behavior
        onSelectionChange([assignment]);
        onClose();
      }
    }
  };





  const handleOpenAssignmentBuilder = () => {
    window.open('/assignment-builder', '_blank');
  };



  const handlePreviewAssignment = (assignmentId) => {
    window.open(`/assignment-builder?id=${assignmentId}&command=edit`, '_blank');
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase());
    
    // If opened from ProjectBuilder, filter out assignments with reference_table === 'components'
    if (isProjectBuilder && assignment.reference_table === 'components') {
      return false;
    }
    
    return matchesSearch;
  });

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes asm-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .asm-tb:hover:not(:disabled) {
            background: #f8fafc !important;
            border-color: #cbd5e1 !important;
            color: #0f172a !important;
          }
          .asm-preview-ico:hover {
            background: #f1f5f9 !important;
            border-color: #cbd5e1 !important;
            color: #334155 !important;
          }
          .asm-item:hover {
            background: #f9fafb;
          }
        `}
      </style>
      <div style={modalOverlay}>
        <div style={modalContent}>
        <div style={modalHeader}>
          <div style={{ minWidth: 0 }}>
            <h2 style={modalTitle}>
              {isProjectBuilder ? (allowMultiple ? 'Select assignments' : 'Select assignment') : 'Select assignment'}
            </h2>
            <p style={modalSubtitle}>Click an assignment to select it.</p>
          </div>
          <button type="button" onClick={() => {
            // Ensure final selection is saved when closing
            if (allowMultiple && localSelectedAssignments.length !== selectedAssignments.length) {
              onSelectionChange(localSelectedAssignments);
            }
            onClose();
          }} style={closeButton}>×</button>
        </div>

        <div style={searchContainer}>
          <input
            type="text"
            placeholder="Search assignments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInput}
          />
        </div>

        <div style={buttonContainer}>
          <button
            type="button"
            className="asm-tb"
            onClick={handleOpenAssignmentBuilder}
            style={iconToolbarButton}
            title="Create new assignment (opens Assignment Builder in a new tab)"
          >
            <IconPlus />
          </button>
          <button
            type="button"
            className="asm-tb"
            onClick={handleRefreshAssignments}
            style={{
              ...iconToolbarButton,
              ...(isRefreshing ? { opacity: 0.55, cursor: 'not-allowed' } : {}),
            }}
            disabled={isRefreshing}
            title={isRefreshing ? 'Refreshing…' : 'Reload assignments from server'}
          >
            <IconRefresh spinning={isRefreshing} />
          </button>
        </div>

        {refreshStatus && (
          <div style={{
            ...refreshStatusMessage,
            ...(refreshStatus === 'success' ? refreshStatusOk : refreshStatusErr)
          }}>
            {refreshStatus === 'success' ? (
              <>Updated list ({assignments.length} assignments).</>
            ) : (
              <>Could not refresh. Try again.</>
            )}
          </div>
        )}

        {allowMultiple && localSelectedAssignments.length > 0 && (
          <div style={selectedSummary}>
            <strong>Selected ({localSelectedAssignments.length}):</strong> {localSelectedAssignments.map(a => a.title).join(', ')}
          </div>
        )}
        <div style={assignmentsList}>
          {filteredAssignments.length === 0 ? (
            <div style={noResultsText}>
              {searchTerm ? 'No assignments found matching your search.' : 'No assignments available.'}
              {!searchTerm && userAssignments.length === 0 && ' (No assignments created yet)'}
            </div>
          ) : (
            filteredAssignments.map((assignment) => {
              const isSelected = localSelectedAssignments.some(a => a.id === assignment.id);
              return (
                <div
                  key={assignment.id}
                  className="asm-item"
                  onClick={() => handleAssignmentToggle(assignment)}
                  style={{
                    ...assignmentItem,
                    ...(allowMultiple && isSelected ? assignmentItemSelected : {})
                  }}
                >
                <div style={assignmentInfo}>
                  <div style={assignmentTitle}>
                    {assignment.title}
                    {allowMultiple && isSelected && (
                      <span style={{ marginLeft: '8px', color: '#64748b', fontWeight: 600, fontSize: '0.85rem' }}>✓</span>
                    )}
                  </div>
                  <div style={assignmentDetails}>
                    Hours: {assignment.hours || 0} · 
                    Type: {assignment.gradingresulttype || 'N/A'} · 
                    Questions: {assignment.questions?.length || 0}
                  </div>
                  <div style={assignmentDates}>
                    Created: {assignment.createddate ? dateTimeToMonDDYYYYWithTime(assignment.createddate, true) : 'N/A'} · 
                    Updated: {assignment.updateddate ? dateTimeToMonDDYYYYWithTime(assignment.updateddate, true) : 'N/A'}
                  </div>
                </div>
                <button
                  type="button"
                  className="asm-preview-ico"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreviewAssignment(assignment.id);
                  }}
                  style={previewIconButton}
                  title="Preview — open in Assignment Builder (new tab)"
                >
                  <IconOpenNew />
                </button>
              </div>
            );
            })
          )}
        </div>

        {/* Taxonomy Inheritance Popup - ProjectBuilder (Detailed) */}
        {showInheritancePopup && isProjectBuilder && (
          <div style={inheritancePopupOverlay}>
            <div style={inheritancePopupContent}>
              <div style={inheritancePopupHeader}>
                <h3 style={inheritancePopupTitle}>🎯 Inherit Assignment Data?</h3>
                <button 
                  onClick={() => {
                    setShowInheritancePopup(false);
                    // When X is clicked, apply assignments without inheritance
                    onSelectionChange(pendingAssignments);
                    onClose(); // Close the modal after selection
                  }} 
                  style={inheritancePopupCloseButton}
                >
                  ×
                </button>
              </div>
              
              <div style={inheritancePopupBody}>
                <p style={inheritancePopupText}>
                  Would you like this project to automatically inherit the taxonomy and concentrations from the selected assignments?
                </p>
                
                <div style={inheritanceInfo}>
                  <span style={inheritanceInfoIcon}>ℹ️</span>
                  <span style={inheritanceInfoText}>
                    {pendingAssignments.filter(a => a.taxonomy && a.taxonomy.length > 0).length} of {pendingAssignments.length} assignments have taxonomy data to inherit.
                  </span>
                </div>
                
                {pendingAssignments.filter(a => !a.taxonomy || a.taxonomy.length === 0).length > 0 && (
                  <div style={inheritanceWarning}>
                    <span style={inheritanceWarningIcon}>⚠️</span>
                    <span style={inheritanceWarningText}>
                      Some assignments don't have taxonomy data yet. You can add taxonomy to assignments in the{' '}
                      <button 
                        onClick={() => window.open('/assignment-builder', '_blank')}
                        style={inheritanceLinkButton}
                      >
                        Assignment Builder
                      </button>.
                    </span>
                  </div>
                )}
                
                <div style={inheritanceDetails}>
                  <strong>Selected Assignments:</strong>
                  <ul style={inheritanceList}>
                    {pendingAssignments.map(assignment => (
                      <li key={assignment.id} style={inheritanceListItem}>
                        {assignment.title}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={inheritanceBenefits}>
                  <div style={inheritanceBenefit}>
                    <span style={inheritanceIcon}>✅</span>
                    <span>Automatically populate Standards, Skills, and Objectives</span>
                  </div>
                  <div style={inheritanceBenefit}>
                    <span style={inheritanceIcon}>✅</span>
                    <span>Include relevant concentrations</span>
                  </div>
                  <div style={inheritanceBenefit}>
                    <span style={inheritanceIcon}>✅</span>
                    <span>Save time on manual selection</span>
                  </div>
                </div>
              </div>

              <div style={inheritancePopupActions}>
                <button 
                  onClick={() => {
                    setShowInheritancePopup(false);
                    // Don't inherit - just use the assignments as-is
                    onSelectionChange(pendingAssignments);
                    onClose(); // Close the modal after selection
                  }} 
                  style={inheritanceDeclineButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  No, Keep Manual
                </button>
                <button 
                  onClick={() => {
                    setShowInheritancePopup(false);
                    // Inherit taxonomy and concentrations
                    onTaxonomyInheritance(pendingAssignments);
                    onSelectionChange(pendingAssignments);
                    onClose(); // Close the modal after selection
                  }} 
                  style={inheritanceAcceptButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                >
                  Yes, Inherit All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Taxonomy Inheritance Popup - CourseBuilder (Simplified) */}
        {showInheritancePopup && !isProjectBuilder && (
          <div style={inheritancePopupOverlay}>
            <div style={inheritancePopupContent}>
              <div style={inheritancePopupHeader}>
                <h3 style={inheritancePopupTitle}>🎯 Inherit Assignment Data?</h3>
                <button 
                  onClick={() => {
                    setShowInheritancePopup(false);
                    // When X is clicked, apply assignment without inheritance
                    onSelectionChange(pendingAssignments);
                    onClose(); // Close the modal after selection
                  }} 
                  style={inheritancePopupCloseButton}
                >
                  ×
                </button>
              </div>
              
              <div style={inheritancePopupBody}>
                <p style={inheritancePopupText}>
                  Would you like to inherit the taxonomy and concentrations from this assignment?
                </p>
              </div>

              <div style={inheritancePopupActions}>
                <button 
                  onClick={() => {
                    setShowInheritancePopup(false);
                    // Don't inherit - just use the assignment as-is
                    onSelectionChange(pendingAssignments);
                    onClose(); // Close the modal after selection
                  }} 
                  style={inheritanceDeclineButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
                >
                  No, Keep Manual
                </button>
                <button 
                  onClick={() => {
                    setShowInheritancePopup(false);
                    // Inherit taxonomy and concentrations
                    onTaxonomyInheritance(pendingAssignments);
                    onSelectionChange(pendingAssignments);
                    onClose(); // Close the modal after selection
                  }} 
                  style={inheritanceAcceptButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#218838'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#28a745'}
                >
                  Yes, Inherit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10000
};

const modalContent = {
  backgroundColor: 'white',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.15)',
  border: '1px solid #e5e7eb'
};

const modalHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '1rem 1.25rem',
  borderBottom: '1px solid #e5e7eb'
};

const modalTitle = {
  margin: 0,
  color: '#0f172a',
  fontSize: '1.125rem',
  fontWeight: 600,
  letterSpacing: '-0.02em'
};

const modalSubtitle = {
  margin: '6px 0 0 0',
  fontSize: '0.8125rem',
  color: '#64748b',
  lineHeight: 1.4,
  fontWeight: 400
};

const closeButton = {
  background: 'none',
  border: 'none',
  fontSize: '2rem',
  cursor: 'pointer',
  color: '#666',
  padding: 0,
  width: '30px',
  height: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const searchContainer = {
  padding: '0.75rem 1.25rem',
  borderBottom: '1px solid #e5e7eb'
};

const searchInput = {
  width: '100%',
  padding: '0.5rem 0.65rem',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '0.875rem',
  background: '#fafafa',
  color: '#0f172a'
};

const buttonContainer = {
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  padding: '0.5rem 1.25rem',
  borderBottom: '1px solid #e5e7eb',
  flexWrap: 'wrap'
};

const iconToolbarButton = {
  width: '36px',
  height: '36px',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  background: '#fff',
  color: '#475569',
  cursor: 'pointer',
  transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease'
};



const assignmentsList = {
  flex: 1,
  overflowY: 'auto',
  padding: '0.75rem 1.25rem',
  maxHeight: '400px'
};

const assignmentItem = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '0.6rem 0.65rem',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  marginBottom: '6px',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, border-color 0.15s ease',
  backgroundColor: '#fff'
};

const assignmentItemSelected = {
  backgroundColor: '#f8fafc',
  borderColor: '#cbd5e1'
};



const assignmentInfo = {
  flex: 1
};

const assignmentTitle = {
  fontWeight: 600,
  color: '#1e293b',
  marginBottom: '0.2rem',
  fontSize: '0.9rem'
};

const assignmentDetails = {
  fontSize: '0.78rem',
  color: '#64748b'
};

const assignmentDates = {
  fontSize: '0.72rem',
  color: '#94a3b8',
  marginTop: '0.2rem',
  fontStyle: 'normal'
};





const noResultsText = {
  textAlign: 'center',
  color: '#666',
  padding: '2rem'
};




const selectedSummary = {
  padding: '0.5rem 0.75rem',
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  margin: '0 1.25rem',
  fontSize: '0.8125rem',
  color: '#475569'
};



const previewIconButton = {
  flexShrink: 0,
  width: '34px',
  height: '34px',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  background: '#fff',
  color: '#64748b',
  cursor: 'pointer',
  transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease'
};

// Inheritance Popup Styles
const inheritancePopupOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10001
};

const inheritancePopupContent = {
  backgroundColor: 'white',
  borderRadius: '12px',
  width: '90%',
  maxWidth: '500px',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
};

const inheritancePopupHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.5rem 1.5rem 1rem 1.5rem',
  borderBottom: '1px solid #e0e0e0'
};

const inheritancePopupTitle = {
  margin: 0,
  color: '#1D4E89',
  fontSize: '1.25rem',
  fontWeight: '600'
};

const inheritancePopupCloseButton = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#666',
  padding: 0,
  width: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const inheritancePopupBody = {
  padding: '1rem 1.5rem',
  flex: 1
};

const inheritancePopupText = {
  margin: '0 0 1rem 0',
  fontSize: '1rem',
  color: '#333',
  lineHeight: '1.5'
};

const inheritanceInfo = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '1rem',
  padding: '0.75rem',
  backgroundColor: '#e3f2fd',
  border: '1px solid #bbdefb',
  borderRadius: '6px',
  fontSize: '0.9rem'
};

const inheritanceInfoIcon = {
  fontSize: '1rem'
};

const inheritanceInfoText = {
  color: '#1565c0',
  fontWeight: '500'
};

const inheritanceWarning = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '1rem',
  padding: '0.75rem',
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeaa7',
  borderRadius: '6px',
  fontSize: '0.9rem'
};

const inheritanceWarningIcon = {
  fontSize: '1rem'
};

const inheritanceWarningText = {
  color: '#856404',
  fontWeight: '500'
};

const inheritanceLinkButton = {
  background: 'none',
  border: 'none',
  color: '#1D4E89',
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  padding: 0,
  margin: 0
};

const inheritanceDetails = {
  marginBottom: '1.5rem'
};

const inheritanceList = {
  margin: '0.5rem 0 0 0',
  paddingLeft: '1.5rem'
};

const inheritanceListItem = {
  marginBottom: '0.25rem',
  color: '#1D4E89',
  fontWeight: '500'
};

const inheritanceBenefits = {
  marginBottom: '1.5rem'
};

const inheritanceBenefit = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '0.5rem',
  fontSize: '0.9rem',
  color: '#555'
};

const inheritanceIcon = {
  fontSize: '1rem'
};

const inheritancePopupActions = {
  display: 'flex',
  gap: '1rem',
  padding: '1rem 1.5rem',
  borderTop: '1px solid #e0e0e0',
  justifyContent: 'flex-end'
};

const inheritanceDeclineButton = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  transition: 'background-color 0.2s'
};

const inheritanceAcceptButton = {
  padding: '0.75rem 1.5rem',
  backgroundColor: '#28a745',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: '500',
  transition: 'background-color 0.2s'
};

const refreshStatusMessage = {
  padding: '0.45rem 0.75rem',
  borderRadius: '8px',
  margin: '0 1.25rem 0.75rem 1.25rem',
  fontSize: '0.78rem',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem'
};

const refreshStatusOk = {
  backgroundColor: '#f0fdf4',
  border: '1px solid #bbf7d0',
  color: '#166534'
};

const refreshStatusErr = {
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b'
};

export default AssignmentSelectorModal; 